import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { createUser, fetchUsers, fetchUserStats, UserRecord, UsersPage as UsersPageData, type EmailStatusFilter, type PlanFilter, type UserStats } from "@/lib/api";
import { useAuth } from "@/app/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn, formatDuration } from "@/lib/utils";
import { SlidePanel } from "@/components/SlidePanel";
import { UserDetailPanel } from "@/views/UserDetailPanel";
import { OrgRoleBadge } from "@/components/OrgRoleBadge";
import { Shield, User } from "@/components/icons";

const PAGE_LIMIT = 50;
const SEARCH_DEBOUNCE_MS = 300;
const ROW_HEIGHT_PX = 44;
const SCROLL_AREA_HEIGHT_PX = 600;
// Column widths for the virtualized rows. Keep header + body in sync.
// Username | Name | Email | Account ID | Role | Plan | Created | Last Login | Sessions
// The Role column carries the system role plus any org-membership role badges,
// so it needs room for a couple of badges side by side. Plan shows the
// EFFECTIVE tier (a free grant reads as "Prime · free").
const USERS_GRID =
  "minmax(170px, 1.3fr) minmax(110px, 0.9fr) minmax(170px, 1.3fr) minmax(120px, 0.9fr) minmax(160px, 1.1fr) " +
  "minmax(104px, 0.8fr) minmax(140px, 1fr) minmax(140px, 1fr) 80px";
// Cap React Query's cache too — at 40 pages × 50 rows = 2,000 user objects in
// memory at most. Older pages get evicted as the user scrolls past them.
const MAX_PAGES_IN_CACHE = 40;

/**
 * Collapse a user's org memberships into at most two distinct role badges
 * (Manager / Driver), each tooltip listing the orgs where that role is held.
 * A user can be a manager in one org and a driver in another, so both may show.
 */
function orgRoleSummary(
  memberships?: UserRecord["organization_memberships"],
): Array<{ role: "manager" | "driver"; title: string }> {
  if (!memberships || memberships.length === 0) return [];
  const byRole: Record<"manager" | "driver", string[]> = { manager: [], driver: [] };
  for (const m of memberships) {
    if (m.role === "manager" || m.role === "driver") byRole[m.role].push(m.organization_name);
  }
  const out: Array<{ role: "manager" | "driver"; title: string }> = [];
  (["manager", "driver"] as const).forEach((role) => {
    if (byRole[role].length > 0) {
      const label = role === "manager" ? "Manager of" : "Driver in";
      out.push({ role, title: `${label}: ${byRole[role].join(", ")}` });
    }
  });
  return out;
}

/**
 * The user's EFFECTIVE plan, as a single scannable badge:
 *   - active admin comp (base free + prime_grant_active) → "Prime · free"
 *     (info), tooltip shows the expiry so a comp is distinguishable from a
 *     paying subscriber;
 *   - a real paid base tier (prime / commercial) → that tier (success);
 *   - otherwise → "Free" (muted neutral).
 * Billing/reporting must still key off base_tier, not this display.
 */
function PlanBadge({ user: u }: { user: UserRecord }) {
  if (u.prime_grant_active) {
    const until = u.prime_until ? new Date(u.prime_until).toLocaleString() : null;
    return (
      <Badge variant="info" title={until ? `Free Prime until ${until}` : "Free Prime grant"}>
        Prime · free
      </Badge>
    );
  }
  const tier = u.tier || "free";
  if (tier !== "free") {
    return <Badge variant="success" className="capitalize">{tier}</Badge>;
  }
  return <Badge variant="neutral">Free</Badge>;
}

export function UsersPage() {
  const { user: currentUser } = useAuth();

  // Debounced search term so we don't refire on every keystroke.
  const [searchInput, setSearchInput] = React.useState("");
  const [searchDebounced, setSearchDebounced] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Role filter — lets an admin audit who has privileged access.
  const [roleFilter, setRoleFilter] = React.useState<"all" | "admin" | "user">("all");
  // Recovery-channel triage filter — find accounts that can't self-reset.
  const [emailFilter, setEmailFilter] = React.useState<"all" | EmailStatusFilter>("all");
  // Subscription-plan filter — list who is free / prime / commercial, or just
  // the comped users (an active free-Prime grant).
  const [planFilter, setPlanFilter] = React.useState<"all" | PlanFilter>("all");

  const q = useInfiniteQuery<UsersPageData>({
    queryKey: ["users", searchDebounced, roleFilter, emailFilter, planFilter],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchUsers({
        q: searchDebounced || undefined,
        cursor: (pageParam as number) || undefined,
        limit: PAGE_LIMIT,
        role: roleFilter === "all" ? undefined : roleFilter,
        emailStatus: emailFilter === "all" ? undefined : emailFilter,
        plan: planFilter === "all" ? undefined : planFilter,
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // Cap memory: keep at most N pages in cache; oldest are evicted on next fetch.
    maxPages: MAX_PAGES_IN_CACHE,
    refetchInterval: 20_000,
  });

  // Aggregate account counts for the header. Served from the server's
  // in-memory cache with a short TTL, so a slow refetch is cheap; fetch it
  // once and let it refresh occasionally rather than polling hard.
  const statsQuery = useQuery<UserStats>({
    queryKey: ["user-stats"],
    queryFn: fetchUserStats,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Flatten all loaded pages into one list for the virtualizer.
  const allUsers = useMemo(
    () => q.data?.pages.flatMap((p) => p.users) ?? [],
    [q.data]
  );

  // Reset search resets scroll position so virtualizer doesn't try to keep
  // an out-of-bounds offset.
  const scrollParentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollParentRef.current) scrollParentRef.current.scrollTop = 0;
  }, [searchDebounced, roleFilter, emailFilter, planFilter]);

  // Row virtualizer — only renders rows currently in the viewport (+ overscan).
  const virtualizer = useVirtualizer({
    count: allUsers.length,
    getScrollElement: () => scrollParentRef.current,
    estimateSize: () => ROW_HEIGHT_PX,
    overscan: 8,
  });

  // Fetch next page when the bottom of the loaded set scrolls into view.
  const virtualItems = virtualizer.getVirtualItems();
  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= allUsers.length - 5 && q.hasNextPage && !q.isFetchingNextPage) {
      q.fetchNextPage();
    }
  }, [virtualItems, allUsers.length, q.hasNextPage, q.isFetchingNextPage]);

  const [username, setUsername] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [role, setRole] = React.useState<"admin" | "user">("user");

  // Detail panel
  const [selectedUser, setSelectedUser] = React.useState<UserRecord | null>(() => {
    const saved = localStorage.getItem("usersSelectedPanel");
    return saved ? JSON.parse(saved) : null;
  });
  const handleUserClick = useCallback((u: UserRecord) => {
    setSelectedUser(u);
    localStorage.setItem("usersSelectedPanel", JSON.stringify(u));
  }, []);
  // Double-click opens the full user details page (single click = panel).
  const navigate = useNavigate();
  const handleUserOpen = useCallback(
    (u: UserRecord) => navigate(`/users/${u.id}`),
    [navigate],
  );
  const closePanel = useCallback(() => {
    setSelectedUser(null);
    localStorage.removeItem("usersSelectedPanel");
  }, []);

  // Keep selected user in sync with refreshed data
  React.useEffect(() => {
    if (selectedUser && allUsers.length) {
      const updated = allUsers.find((u) => u.id === selectedUser.id);
      if (updated) setSelectedUser(updated);
      // (don't auto-close when not in the current loaded slice — the user may
      // have scrolled past it, and we don't want to drop their selection)
    }
  }, [allUsers]);

  const createM = useMutation({
    mutationFn: () =>
      createUser({ username, password, role, email: email.trim() || undefined }),
    onSuccess: () => {
      toast.success("User created");
      // Clear the whole form so an accidental second click can't re-submit
      // the same user (which would just 400 "already exists").
      setUsername("");
      setEmail("");
      setPassword("");
      setRole("user");
      q.refetch();
    },
    onError: (e: any) =>
      toast.error(
        e?.message?.includes("email_taken")
          ? "That email is already used by another account."
          : e?.message || "Create failed",
      ),
  });

  const isSelf = useCallback(
    (u: UserRecord) => currentUser?.id === u.id,
    [currentUser?.id]
  );

  return (
    <div className="space-y-5 pt-[86px]">
      <div>
        <h1 className="text-2xl font-semibold">Users</h1>
        <div className="text-sm opacity-70">Manage console users</div>
      </div>

      {/* Account totals. "Active" = accounts that have logged in at least once
          (last_login set) — NOT online chargers. Counts come from a cached,
          DB-free server aggregate (GET /api/users/stats). */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-5">
        {([
          { label: "Total users", value: statsQuery.data?.total_users },
          { label: "Active (logged in)", value: statsQuery.data?.active_users },
          { label: "Never logged in", value: statsQuery.data?.never_logged_in },
          { label: "Admins", value: statsQuery.data?.admins },
          { label: "Disabled", value: statsQuery.data?.disabled },
        ] as const).map((stat) => (
          <Card key={stat.label}>
            <CardContent className="py-4">
              <div className="text-xs uppercase tracking-wide opacity-70">{stat.label}</div>
              <div className="mt-1 text-2xl font-semibold tabular-nums">
                {statsQuery.isError
                  ? "—"
                  : stat.value === undefined
                    ? "…"
                    : stat.value.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create User Form */}
      <Card>
        <CardHeader>
          <CardTitle>Create user</CardTitle>
          <CardDescription>Add a new console account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[1fr_1fr_1fr_auto_auto] items-end gap-3">
            <div>
              <Label>Username</Label>
              {/* autoComplete off + non-login name: without these the browser
                  autofills the admin's SAVED login credentials into this
                  create-form, priming an accidental "Create". new-password on
                  the password field is what actually makes Chrome stop pairing
                  the two fields with the saved login. */}
              <Input
                className="mt-1.5"
                name="create-user-username"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
              />
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input
                className="mt-1.5"
                type="email"
                name="create-user-email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <div>
              <Label>Password (min 10 chars)</Label>
              <Input
                className="mt-1.5"
                type="password"
                name="create-user-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as "admin" | "user")}>
                <SelectTrigger className="mt-1.5 w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    <span className="inline-flex items-center gap-2">
                      <User className="h-4 w-4 opacity-60" />
                      User
                    </span>
                  </SelectItem>
                  <SelectItem value="admin">
                    <span className="inline-flex items-center gap-2">
                      <Shield className="h-4 w-4 opacity-60" />
                      Admin
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              className="h-9"
              onClick={() => createM.mutate()}
              disabled={createM.isPending || !username || password.length < 10}
            >
              Create
            </Button>
          </div>
          {/* Explain why the button is disabled — otherwise it just looks broken. */}
          {(!username || password.length < 10) && (
            <div className="mt-2 text-xs opacity-60">
              {!username
                ? "Enter a username to create a user."
                : `Password must be at least 10 characters (${password.length}/10).`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Table + Detail Panel */}
      <div className="flex gap-4 items-stretch">
        <div className="flex-1 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle>Existing users</CardTitle>
              <CardDescription>
                Search by email, name, or account ID. Rows render only while
                visible; more load as you scroll.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex gap-2">
                <Input
                  className="flex-1"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search email, name, or account ID…"
                />
                <Select
                  value={roleFilter}
                  onValueChange={(v) => setRoleFilter(v as "all" | "admin" | "user")}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="admin">
                      <span className="inline-flex items-center gap-2">
                        <Shield className="h-4 w-4 opacity-60" />
                        Admins
                      </span>
                    </SelectItem>
                    <SelectItem value="user">
                      <span className="inline-flex items-center gap-2">
                        <User className="h-4 w-4 opacity-60" />
                        Users
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={emailFilter}
                  onValueChange={(v) => setEmailFilter(v as "all" | EmailStatusFilter)}
                >
                  <SelectTrigger className="w-[190px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any email status</SelectItem>
                    <SelectItem value="missing">No email</SelectItem>
                    <SelectItem value="unverified">Email unverified</SelectItem>
                    <SelectItem value="verified">Email verified</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={planFilter}
                  onValueChange={(v) => setPlanFilter(v as "all" | PlanFilter)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any plan</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="prime">Prime</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="grant">Free Prime grants</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {q.isError ? (
                <div className="text-sm text-error">Failed to load users</div>
              ) : (
                <>
                  {/* Header row (sticky, outside the virtualized scroller). */}
                  <div
                    className="grid items-center gap-2 px-3 py-2 text-xs font-medium opacity-70 border-b"
                    style={{ gridTemplateColumns: USERS_GRID }}
                  >
                    <div>Username</div>
                    <div>Name</div>
                    <div>Email</div>
                    <div>Account ID</div>
                    <div>Role</div>
                    <div>Plan</div>
                    <div>Created</div>
                    <div>Last Login</div>
                    <div className="text-right pr-2">Sessions</div>
                  </div>

                  {/* Virtualized body — only renders ~15 rows in DOM at a time
                      (≈600px / 44px row + 8 overscan). */}
                  <div
                    ref={scrollParentRef}
                    className="overflow-y-auto border-t border-transparent"
                    style={{ height: SCROLL_AREA_HEIGHT_PX }}
                  >
                    {q.isLoading && allUsers.length === 0 ? (
                      <div className="py-10 text-center text-sm opacity-60">Loading…</div>
                    ) : allUsers.length === 0 ? (
                      <div className="py-10 text-center text-sm opacity-60">
                        {searchDebounced ? "No matches" : "No users"}
                      </div>
                    ) : (
                      <div
                        style={{
                          height: virtualizer.getTotalSize(),
                          width: "100%",
                          position: "relative",
                        }}
                      >
                        {virtualItems.map((vi) => {
                          const u = allUsers[vi.index];
                          if (!u) return null;
                          const selected = selectedUser?.id === u.id;
                          const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
                          return (
                            <div
                              key={u.id}
                              className={cn(
                                "absolute top-0 left-0 w-full grid items-center gap-2 px-3 text-sm cursor-pointer select-none hover:bg-muted/60",
                                selected && "bg-primary/10"
                              )}
                              style={{
                                gridTemplateColumns: USERS_GRID,
                                height: vi.size,
                                transform: `translateY(${vi.start}px)`,
                              }}
                              onClick={() => handleUserClick(u)}
                              onDoubleClick={() => handleUserOpen(u)}
                            >
                              <div className="truncate">
                                <span className={cn("font-medium", u.disabled && "opacity-50 line-through")}>
                                  {u.username}
                                </span>
                                {isSelf(u) && (
                                  <Badge variant="info" className="ml-2 badge-sm">You</Badge>
                                )}
                                {u.disabled && (
                                  <Badge variant="destructive" className="ml-2 badge-sm">Disabled</Badge>
                                )}
                                {u.locked && (
                                  <Badge variant="warning" className="ml-2 badge-sm">
                                    Locked{u.lockout_remaining_seconds
                                      ? ` ${formatDuration(u.lockout_remaining_seconds)}`
                                      : ""}
                                  </Badge>
                                )}
                              </div>
                              <div className="truncate">
                                {fullName || <span className="opacity-50">—</span>}
                              </div>
                              <div className="truncate flex items-center gap-1.5">
                                {u.email ? (
                                  <>
                                    <span
                                      className={cn(
                                        "h-1.5 w-1.5 shrink-0 rounded-full",
                                        u.email_verified ? "bg-success" : "bg-warning"
                                      )}
                                      title={u.email_verified ? "verified" : "unverified"}
                                    />
                                    <span className="truncate text-xs">{u.email}</span>
                                  </>
                                ) : (
                                  <span className="opacity-50">—</span>
                                )}
                              </div>
                              <div className="truncate font-mono text-xs">
                                {u.account_id || "—"}
                              </div>
                              <div className="flex flex-wrap items-center gap-1">
                                <Badge variant={u.role === "admin" ? "primary" : "neutral"}>
                                  {u.role}
                                </Badge>
                                {orgRoleSummary(u.organization_memberships).map((s) => (
                                  <OrgRoleBadge
                                    key={s.role}
                                    role={s.role}
                                    className="cursor-default"
                                    title={s.title}
                                  />
                                ))}
                              </div>
                              <div>
                                <PlanBadge user={u} />
                              </div>
                              <div className="text-xs">
                                {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                              </div>
                              <div className="text-xs">
                                {u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}
                              </div>
                              <div className="text-right pr-2">{u.session_count ?? 0}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {q.isFetchingNextPage && (
                    <div className="py-2 text-center text-xs opacity-60">Loading more…</div>
                  )}
                  {!q.hasNextPage && allUsers.length > 0 && (
                    <div className="py-2 text-center text-xs opacity-50">
                      {allUsers.length.toLocaleString()} loaded
                      {searchDebounced ? ` for "${searchDebounced}"` : ""}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail panel */}
        <SlidePanel
          open={!!selectedUser}
          onClose={closePanel}
          title="User Details"
        >
          {selectedUser && (
            <UserDetailPanel
              user={selectedUser}
              isSelf={isSelf(selectedUser)}
              onDeleted={closePanel}
            />
          )}
        </SlidePanel>
      </div>
    </div>
  );
}
